import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { ColumnDef } from '@3ddv/software-division-components/backoffice/table';
import { ButtonComponent } from '@3ddv/software-division-components/generic/button';
import { AppDialogService } from '../layout/dialog/app-dialog.service';
import { ApiResponse } from '../models/api.model';
import { ServiceChargesModalComponent } from './modal/service-charges-modal.component';
import { ServiceCharge } from './models/service-charge.model';
import { ServiceChargesService } from './services/service-charges.service';
import { GsTableComponent } from '../../shared/ui/table/gs-table.component';
import { AuthStore } from '../../core/stores/auth.store';
import { buildServiceChargeColumns } from './service-charges.columns';

@Component({
  selector: 'app-service-charges',
  templateUrl: './service-charges.component.html',
  imports: [ButtonComponent, GsTableComponent],
})
export class ServiceChargesComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly dialogService = inject(AppDialogService);
  protected readonly authStore = inject(AuthStore);
  private readonly serviceChargesService = inject(ServiceChargesService);
  private readonly toastr = inject(ToastrService);

  private subscriptions: Array<Subscription>;
  public serviceCharges: ServiceCharge[] = [];
  public columns: ColumnDef<ServiceCharge>[] = [];

  ngOnInit() {
    this.serviceCharges = this.route.snapshot.data['serviceCharges'];
    this.columns = buildServiceChargeColumns(
      charge => this.getDescription(charge),
      charge => this.openModal(charge),
      charge => this.deleteServiceCharge(charge),
      this.authStore.canEdit()
    );
  }

  ngOnDestroy(): void {
    this.subscriptions?.forEach(o => o.unsubscribe());
  }

  getDescription(charge: ServiceCharge): string {
    return `${charge.amount} ${charge.amountUnit === 'percent' ? '%' : 'USD'} per ${charge.applyPer}, up to ${charge.capAmount} ${charge.capAmountUnit}`;
  }

  openModal(selectedServiceCharge?: ServiceCharge) {
    const { closed$ } = this.dialogService.open<
      ServiceChargesModalComponent,
      ServiceCharge[]
    >(ServiceChargesModalComponent, {
      size: 'lg',
      inputs: {
        selectedServiceCharge: selectedServiceCharge || null,
        serviceCharges: this.serviceCharges,
      },
      providers: [
        {
          provide: ServiceChargesService,
          useValue: this.serviceChargesService,
        },
      ],
    });

    closed$.subscribe(serviceCharges => {
      if (serviceCharges) {
        this.serviceCharges = serviceCharges;
      }
    });
  }

  deleteServiceCharge(serviceCharge: ServiceCharge) {
    if (confirm('Are you sure you want to delete this service charge?')) {
      const serviceCharges = this.serviceCharges.filter(
        c => c.id !== serviceCharge.id
      );

      this.serviceChargesService
        .createOrUpdateServiceCharge(serviceCharges)
        .subscribe({
          next: (res: ApiResponse<ServiceCharge[]>) => {
            this.serviceCharges = res.data;
            this.toastr.success('Service charge deleted successfully');
          },
          error: () => this.toastr.error('Error deleting service charge'),
        });
    }
  }
}
